import {
  sharedDailySource,
  sharedMonthlySource,
  sharedWeeklySource,
} from "./rounds";

/**
 * Idle prefetch of the Round *indexes* the player has not selected yet.
 *
 * What a timeframe switch waits on is the thing that has to arrive before any
 * Round can be chosen, and since GRA-152 that is the index, not the bank: a
 * hundred kilobytes rather than tens of megabytes. Warming it during idle time
 * makes the switch cost one shard, the same as any other Round.
 *
 * Prefetching the shards too would be exactly the speculative multi-megabyte
 * pull the on-demand seam exists to stop, so it is deliberately not done: a
 * shard is only ever fetched for a Round somebody is about to play.
 *
 * Everything here goes through the `shared*Source` promise caches, so a
 * prefetch and a Loop asking for the same bank share one fetch whichever
 * starts first. Failures are swallowed: a prefetch is an optimisation, and the
 * on-demand path retries and reports for real.
 */

/** The banks a prefetch can warm, named as the plan and the loaders see them. */
export type BankName = "monthly" | "weekly" | "daily";

/**
 * Prefetch order: smallest index first, so the cheap ones are warm within a
 * round or two. Daily is usually already loaded (it is the default timeframe),
 * in which case its turn resolves off the cache for free.
 */
export const PREFETCH_ORDER: BankName[] = ["monthly", "weekly", "daily"];

/** The indexes small enough to be worth pulling over a slow connection. */
const SMALL_BANKS: BankName[] = ["monthly", "weekly"];

/**
 * The parts of `navigator.connection` (Network Information API) the plan reads.
 * Typed locally because it is not in the DOM lib, and absent entirely on
 * Safari and Firefox — where `undefined` means "no signal", not "slow".
 */
export type ConnectionInfo = {
  saveData?: boolean;
  effectiveType?: string;
};

/**
 * Which indexes to prefetch on this connection, in order.
 *
 * Even a few hundred kilobytes is worth asking about on metered or slow mobile
 * data, so the original gate is kept as it was: Data Saver switches the
 * prefetch off entirely, 2g pulls nothing, and 3g gets only the two small
 * indexes. With no Network Information API at all the full plan runs, which is
 * the desktop and Safari case.
 */
export function prefetchPlan(connection?: ConnectionInfo | null): BankName[] {
  if (!connection) return [...PREFETCH_ORDER];
  if (connection.saveData === true) return [];
  const type = connection.effectiveType;
  if (type === "slow-2g" || type === "2g") return [];
  if (type === "3g") return [...SMALL_BANKS];
  return [...PREFETCH_ORDER];
}

/** Reads the connection hints off a `navigator`, or null where none exist. */
export function readConnection(nav?: Navigator | null): ConnectionInfo | null {
  const connection = (nav as { connection?: ConnectionInfo } | null | undefined)
    ?.connection;
  return connection ?? null;
}

/** Defers `run` until the browser is idle. */
export type IdleScheduler = (run: () => void) => void;

/**
 * `requestIdleCallback` where it exists, a `setTimeout` otherwise (Safari only
 * shipped the callback recently). The timeout on the idle callback is the
 * ceiling on how long a busy tab can starve the prefetch.
 */
export function idleScheduler(win: Window | undefined = globalThis.window): IdleScheduler {
  const request = (win as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void })
    ?.requestIdleCallback;
  if (request) return (run) => request.call(win, run, { timeout: 2000 });
  return (run) => setTimeout(run, 200);
}

/**
 * Warm `names` one at a time, each waiting for idle first. Sequential on
 * purpose: parsing an index still costs a few main-thread milliseconds, and
 * there is no hurry. Resolves once the queue is drained; a bank that fails is
 * skipped, not retried.
 */
export function runPrefetchQueue(
  names: BankName[],
  load: (name: BankName) => Promise<unknown>,
  schedule: IdleScheduler,
): Promise<void> {
  return new Promise((resolve) => {
    let index = 0;
    const step = () => {
      if (index >= names.length) {
        resolve();
        return;
      }
      const name = names[index++];
      schedule(() => {
        void load(name)
          .catch(() => {
            // A prefetch is an optimisation: the on-demand load reports for real.
          })
          .then(step);
      });
    };
    step();
  });
}

/** The index loaders behind each bank name, all promise-cached (see rounds.ts). */
const LOADERS: Record<BankName, (baseUrl: string) => Promise<unknown>> = {
  monthly: sharedMonthlySource,
  weekly: sharedWeeklySource,
  daily: sharedDailySource,
};

let started = false;

/** Runs `run` once some later point has been reached. */
export type LoadWaiter = (run: () => void) => void;

/**
 * Wait for the page's own `load` event before `run`.
 *
 * `requestIdleCallback` alone is not a "later" guarantee: issuing a `fetch`
 * barely occupies the main thread, so the very next idle tick can land before
 * that fetch's response even arrives, which is what let a prior version of
 * this module's idle-scheduled prefetch start racing the initial critical
 * chain instead of following it. `load` does not fire until the document's
 * own resources (the entry script included) have settled, so gating on it
 * first is what keeps the other timeframes' indexes out of that chain.
 *
 * Already-complete (a hot reload, a very fast open racing this call) resolves
 * immediately rather than waiting for an event that already fired.
 */
export function afterPageLoad(win: Window | undefined = globalThis.window): LoadWaiter {
  return (run) => {
    if (!win || win.document.readyState === "complete") {
      run();
      return;
    }
    win.addEventListener("load", () => run(), { once: true });
  };
}

/**
 * Start the idle prefetch, at most once per session. Called once the opening
 * Round is interactive, so the index the player is looking at never queues
 * behind a speculative one, and held behind `waitForLoad` on top of that so
 * the other banks' indexes never appear alongside the initial critical chain.
 */
export function startBankPrefetch(
  baseUrl = "/data",
  waitForLoad: LoadWaiter = afterPageLoad(),
): Promise<void> {
  if (started) return Promise.resolve();
  started = true;
  return new Promise((resolve) => {
    waitForLoad(() => {
      const plan = prefetchPlan(
        readConnection(typeof navigator === "undefined" ? null : navigator),
      );
      void runPrefetchQueue(plan, (name) => LOADERS[name](baseUrl), idleScheduler()).then(
        resolve,
      );
    });
  });
}

/** Forget that the prefetch ran (test isolation). */
export function resetBankPrefetch(): void {
  started = false;
}
